import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { trackPurchase } from "@/lib/analytics";

export default function BillingSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("pending");
    const [info, setInfo] = useState(null);
    const firedRef = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setStatus("error");
            return;
        }
        let attempts = 0;
        const poll = async () => {
            try {
                const { data } = await api.get(`/billing/status/${sessionId}`);
                setInfo(data);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    // Fire purchase event ONCE per Stripe checkout session ID.
                    // The analytics layer also dedupes across page refreshes via localStorage.
                    if (!firedRef.current) {
                        firedRef.current = true;
                        // Prefer the canonical dollar amount we stored locally; fall back to Stripe's amount_total (smallest currency unit).
                        const amountTotal = Number(data.amount_total || 0);
                        const fallbackDollars = amountTotal >= 1000 ? amountTotal / 100 : amountTotal;
                        const value = Number(data.amount ?? fallbackDollars);
                        trackPurchase({
                            transactionId: sessionId,
                            value,
                            currency: (data.currency || "usd").toUpperCase(),
                            tier: data.tier,
                            planKey: data.plan_key,
                            interval: data.interval,
                        });
                    }
                } else if (data.status === "expired") {
                    setStatus("expired");
                } else if (attempts++ < 8) {
                    setTimeout(poll, 2000);
                } else {
                    setStatus("timeout");
                }
            } catch {
                setStatus("error");
            }
        };
        poll();
    }, [sessionId]);

    return (
        <div className="ok-container py-16 max-w-md mx-auto text-center" data-testid="billing-success-page">
            <div className="ok-card p-10">
                {status === "pending" && (
                    <>
                        <Loader2 className="w-10 h-10 mx-auto text-[var(--ok-accent)] animate-spin" />
                        <h1 className="font-display text-2xl mt-4">Confirming payment…</h1>
                        <p className="text-sm opacity-70 mt-2">Don't close this tab.</p>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <CheckCircle2 className="w-12 h-12 mx-auto text-[var(--ok-accent)]" />
                        <h1 className="font-display text-3xl mt-4">Welcome, Founding Witness.</h1>
                        <p className="text-sm opacity-70 mt-2">
                            Your Pro account is active. You've locked in founding member pricing for life.
                        </p>
                        <Link to="/profile" className="ok-btn-primary mt-6 inline-flex" data-testid="billing-success-profile-btn">
                            Go to my account
                        </Link>
                    </>
                )}
                {(status === "expired" || status === "timeout" || status === "error") && (
                    <>
                        <XCircle className="w-12 h-12 mx-auto text-red-500" />
                        <h1 className="font-display text-2xl mt-4">Couldn't confirm payment</h1>
                        <p className="text-sm opacity-70 mt-2">If your card was charged, please refresh in a moment.</p>
                        <Link to="/pricing" className="ok-btn-outline mt-6 inline-flex" data-testid="billing-success-retry-btn">
                            Back to pricing
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
